import React, { useState, useEffect } from 'react';
import { subscribeRoom, leaveRoom, rematch } from '../shared/roomTx';
import { FL_DIFFICULTY, FL_ROOM_STATUS, FL_SCRIPT_LABELS } from '../shared/fiveLeague';
import { getErrorMessage } from '../shared/errorMessages';
import { CyberBackground, NeonText, CyberButton, GlowingBorder } from '../components/CyberEffects';
import AdInterstitial from '../components/AdInterstitial';
import './Result.css';

function Result({ navigate, playerId, roomId }) {
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rematchLoading, setRematchLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAd, setShowAd] = useState(true); // 結果画面表示時に広告を表示

  // ルーム購読
  useEffect(() => {
    if (!roomId) {
      navigate('home');
      return;
    }

    const unsub = subscribeRoom(roomId, (data) => {
      setLoading(false);
      if (!data) {
        navigate('home');
        return;
      }

      // このプレイヤーがルームに参加しているか確認
      const isPlayer = data.players?.some(p => p.playerId === playerId);
      if (!isPlayer) {
        navigate('home');
        return;
      }

      // 再戦でWAITINGに戻ったら待機画面へ
      if (data.status === FL_ROOM_STATUS.WAITING) {
        navigate('wait', { roomId });
        return;
      }

      setRoom(data);
    });

    return () => unsub();
  }, [roomId, playerId, navigate]);

  const handleLeave = async () => {
    await leaveRoom(roomId, playerId);
    navigate('home');
  };

  const handleRematch = async () => {
    if (rematchLoading) return;

    setRematchLoading(true);
    setError('');

    try {
      const result = await rematch(roomId, playerId);

      if (!result.ok) {
        setError(getErrorMessage(result.reason, '再戦の開始に失敗しました'));
      }
      // 成功したらsubscribeRoomが待機画面へ遷移させる
    } catch (e) {
      console.error('[Rematch] Error:', e);
      setError('エラーが発生しました');
    } finally {
      setRematchLoading(false);
    }
  };

  if (loading || !room) {
    return (
      <div className="result-container">
        <div className="loading">読み込み中...</div>
      </div>
    );
  }

  const game = room.game;
  const correctQuestions = game?.correctQuestions || 0;
  const maxQuestions = room.config?.maxQuestions || 5;
  const isPerfectClear = correctQuestions === maxQuestions;
  const isHost = room.hostId === playerId;

  // スコアに応じたメッセージ
  const getMessage = () => {
    if (isPerfectClear) return '🎊 パーフェクトクリア！🎊';
    if (correctQuestions >= 4) return '🎉 すばらしい！';
    if (correctQuestions >= 3) return '😊 よくできました！';
    if (correctQuestions >= 2) return '👍 がんばりました！';
    if (correctQuestions >= 1) return '💪 次こそは！';
    return '😢 ドンマイ！';
  };

  // 問題履歴（正解した問題と失敗した問題の情報）
  const questionHistory = game?.history || [];

  return (
    <div className="result-container animate-fade-in">
      <CyberBackground showParticles={true} />

      {/* ゲーム終了時のインタースティシャル広告 */}
      <AdInterstitial
        slot="RESULT_INTERSTITIAL"
        isVisible={showAd}
        onClose={() => setShowAd(false)}
      />

      <div className="result-content">
        <div className="result-header">
          <h1 className="result-title">
            <NeonText color={isPerfectClear ? 'accent' : 'pink'} size="lg">
              ゲーム終了
            </NeonText>
          </h1>
        </div>

        <GlowingBorder color={isPerfectClear ? 'accent' : 'pink'} className="score-display-wrapper">
          <div className={`score-display ${isPerfectClear ? 'perfect' : ''}`}>
            <span className="score-message">{getMessage()}</span>
            <div className="score-value">
              <span className="score-correct">{correctQuestions}</span>
              <span className="score-separator">/</span>
              <span className="score-total">{maxQuestions}</span>
            </div>
            <span className="score-label">問正解</span>
          </div>
        </GlowingBorder>

        <div className="progress-section">
          <h2 className="section-title">
            <NeonText color="blue" size="sm">問題の結果</NeonText>
          </h2>
          <div className="question-progress-grid">
            {Array.from({ length: (game?.questionIndex || 0) + 1 }, (_, i) => i + 1).map((num) => {
              const isCorrect = num <= correctQuestions;
              const difficulty = FL_DIFFICULTY[num];
              const historyItem = questionHistory[num - 1];

              return (
                <div
                  key={num}
                  className={`progress-card ${isCorrect ? 'correct' : 'incorrect'}`}
                  style={{ animationDelay: `${0.1 * num}s` }}
                >
                  <div className="progress-card-header">
                    <span className="progress-num">第{num}問</span>
                    <span className="progress-status">
                      {isCorrect ? '⭕' : '❌'}
                    </span>
                  </div>
                  <div className="progress-diff">{difficulty?.label}</div>
                  {historyItem && (
                    <>
                      <div className="progress-prompt">{historyItem.prompt}</div>
                      <div className="progress-answer-row">
                        {historyItem.letters?.map((letter, idx) => (
                          <span
                            key={idx}
                            className={`progress-letter ${historyItem.correctMask?.[idx] ? 'letter-correct' : 'letter-incorrect'}`}
                          >
                            {letter || '?'}
                          </span>
                        ))}
                      </div>
                      {!isCorrect && historyItem.answer && (
                        <div className="progress-correct-answer">
                          正解: {historyItem.answer.join('')}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="players-section">
          <h2 className="section-title">
            <NeonText color="purple" size="sm">参加者</NeonText>
          </h2>
          <div className="players-grid">
            {room.players?.map((player, idx) => (
              <GlowingBorder key={player.playerId} color="blue" className="player-item">
                <span className="player-slot">{idx + 1}</span>
                <span className="player-name">
                  {player.nickname}
                  {player.playerId === room.hostId && ' 👑'}
                </span>
              </GlowingBorder>
            ))}
          </div>
        </div>

        {error && <p className="error-message">{error}</p>}

        <div className="result-actions">
          {isHost ? (
            <CyberButton
              variant="primary"
              className="rematch-btn"
              onClick={handleRematch}
              disabled={rematchLoading}
            >
              {rematchLoading ? '準備中...' : '🔄 同じメンバーで再戦'}
            </CyberButton>
          ) : (
            <div className="waiting-rematch">
              <p>ホストが再戦を開始するのを待っています...</p>
            </div>
          )}
          <CyberButton
            variant="accent"
            className="home-btn"
            onClick={handleLeave}
          >
            ホームに戻る
          </CyberButton>
        </div>

        <div className="result-footer">
          <p className="footer-text">お疲れ様でした！</p>
        </div>
      </div>
    </div>
  );
}

export default Result;
